import { inject, Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { ConfigService } from './config-service';
import { UserStore } from '../user/store/user.store';

export interface NectaReloadParams {
  sessionId: string;
  clientId: string | number;
  userCode: string;
}

@Injectable({
  providedIn: 'root'
})
export class IframeCommunicationService {
  private readonly configService = inject(ConfigService);
  private readonly logoutSubject = new Subject<void>();
  private readonly reloadSubject = new Subject<NectaReloadParams>();
  private readonly userStore = inject(UserStore);

  readonly logout$ = this.logoutSubject.asObservable();
  readonly reload$ = this.reloadSubject.asObservable();

  constructor() {
    window.addEventListener('message', this.handleMessage);
  }

  sendLogout(): void {
    this.logoutSubject.next();
  }

  private readonly handleMessage = (event: MessageEvent): void => {
    // DIAGNOSE (temporär): jede necta-Nachricht protokollieren, auch wenn die Origin-Prüfung sie verwirft.
    if (event.data?.type === 'NECTA_LOGOUT' || event.data?.type === 'NECTA_RELOAD') {
      console.debug('[iframe-comm] msg', event.data?.type,
        'origin=', event.origin,
        'fromNecta=', this.isFromNectaIframe(event.origin),
        'param=', event.data?.param);
    }

    if (!this.isFromNectaIframe(event.origin)) return;

    if (event.data?.type === 'NECTA_LOGOUT') {
      this.logoutSubject.next();
    }

    if (event.data?.type === 'NECTA_RELOAD') {
      // Das GWT-necta liefert nach einem (Superuser-)Kostenstellenwechsel die neue
      // Session mit; wir laden das iframe damit neu (statt eine neue Session anzulegen).
      const param = event.data.param;
      if (param?.sessionId) {
        this.reloadSubject.next({
          sessionId: param.sessionId,
          clientId: param.clientId,
          userCode: param.userCode,
        });
      }
    }
  };

  private isFromNectaIframe(origin: string): boolean {
    try {
      // Tenant-ID berücksichtigen, damit bei aktivierter Subdomain (urlNectaClientWithTenantSubdomain)
      // dieselbe URL geprüft wird, mit der das iframe geladen wurde.
      const tenantId = this.userStore.activeTenant()?.tenantId;
      const nectaUrl = this.configService.getUrlNectaClient(tenantId);
      return !!nectaUrl && new URL(nectaUrl).origin === origin;
    } catch {
      return false;
    }
  }
}
